import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export function NewChatLauncher({ open, onOpenChange, defaultPersona, defaultDepth = 2 }) {
    const { t, lang } = useT();
    const navigate = useNavigate();
    const [personas, setPersonas] = useState([]);
    const [persona, setPersona] = useState(defaultPersona || (lang === 'en' ? 'stillroom' : 'samtalepartner'));
    const [depth, setDepth] = useState(defaultDepth);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (open) {
            api.get(`/personas?language=${lang}`).then(r => {
                setPersonas(r.data);
                // Auto-pick first if current persona isn't in list
                if (r.data.length && !r.data.find(p => p.key === persona)) {
                    setPersona(r.data[0].key);
                }
            }).catch(() => {});
            setDepth(defaultDepth);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, lang]);

    const start = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const { data } = await api.post('/chat/sessions', { persona, depth, language: lang });
            navigate(`/app/samtale/${data.session.session_id}`);
            onOpenChange?.(false);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : t('common.error'));
        } finally {
            setCreating(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-bone max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-chat-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{t('launcher.title')}</DialogTitle>
                    <DialogDescription className="text-inkSoft">
                        {t('launcher.subtitle')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 mt-2">
                    <div>
                        <p className="text-sm font-medium text-ink mb-2">{t('launcher.persona.label')}</p>
                        <div className="grid grid-cols-1 gap-2">
                            {personas.map(p => (
                                <button
                                    key={p.key}
                                    onClick={() => setPersona(p.key)}
                                    className={`relative p-3 rounded-xl border-2 text-left text-sm transition-all ${persona === p.key ? 'border-sage bg-sage text-bone' : 'border-sand bg-white hover:border-sage/40'}`}
                                    data-testid={`pick-persona-${p.key}`}
                                >
                                    <div className="flex items-start gap-2">
                                        <div className="text-lg shrink-0">{p.emoji}</div>
                                        <div className="flex-1">
                                            <div className={`font-medium ${persona === p.key ? 'text-bone' : 'text-ink'}`}>{p.label}</div>
                                            <div className={`text-xs mt-0.5 ${persona === p.key ? 'text-bone/85' : 'text-inkSoft'}`}>{p.description}</div>
                                        </div>
                                    </div>
                                    {persona === p.key && (
                                        <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-bone flex items-center justify-center text-sage-dark text-[10px] font-bold">✓</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-baseline justify-between mb-1">
                            <p className="text-sm font-medium text-ink">{t('launcher.depth.label')}</p>
                            <p className="text-xs text-inkSoft" data-testid="depth-label">{t(`launcher.depth.${depth}`)} · {t(`launcher.depth.${depth}.desc`)}</p>
                        </div>
                        <input
                            type="range"
                            min="1"
                            max="4"
                            step="1"
                            value={depth}
                            onChange={(e) => setDepth(parseInt(e.target.value, 10))}
                            className="w-full accent-sage"
                            data-testid="depth-slider"
                        />
                        <div className="flex justify-between text-[10px] text-inkSoft/70 mt-1">
                            <span>{t('launcher.depth.1')}</span>
                            <span>{t('launcher.depth.2')}</span>
                            <span>{t('launcher.depth.3')}</span>
                            <span>{t('launcher.depth.4')}</span>
                        </div>
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button
                        onClick={start}
                        disabled={creating}
                        className="rounded-full bg-sage hover:bg-sage-dark text-bone"
                        data-testid="start-chat-btn"
                    >
                        {creating ? t('launcher.starting') : t('launcher.start')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
